import React, { useState, useEffect } from 'react';
import { useBranding } from '../../contexts/BrandingContext';
import { EmptyStates } from '../../components/ui/empty-state';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { workflowAPI } from '../../utils/crmApi';
import { 
    BoltIcon,
    PlusIcon,
    PlayIcon,
    PauseIcon,
    StopIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    ChartBarIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge,
    TableAvatar
} from '../../components/kibo-ui/table';

const Workflows = () => {
    const { branding } = useBranding();
    const navigate = useNavigate();
    const { openDialog } = useDialog();
    const [workflows, setWorkflows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20 });
    const [selectedWorkflows, setSelectedWorkflows] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);

    // Workflow status colors
    const getStatusColor = (status) => {
        const colors = {
            'draft': 'bg-muted text-foreground',
            'active': 'bg-green-100 text-green-700',
            'paused': 'bg-yellow-100 text-yellow-700',
            'inactive': 'bg-red-100 text-red-700',
            'error': 'bg-red-100 text-red-700'
        };
        return colors[status] || 'bg-muted text-foreground';
    };

    // Workflow type icons
    const getTypeIcon = (type) => {
        const icons = {
            'lead_nurturing': '🌱',
            'client_onboarding': '👋',
            'task_automation': '✅',
            'email_sequences': '📧',
            'follow_up': '🔄',
            'reminder': '⏰',
            'data_sync': '🔄',
            'notification': '🔔',
            'approval': '✅',
            'escalation': '⬆️'
        };
        return icons[type] || '⚡';
    };

    useEffect(() => {
        loadWorkflows();
    }, [search, statusFilter, typeFilter, pagination.page]);

    const loadWorkflows = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(search && { search }),
                ...(statusFilter && { status: statusFilter }),
                ...(typeFilter && { category: typeFilter })
            };

            const response = await workflowAPI.getAll(params);
            if (response.data.success) {
                setWorkflows(response.data.data);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error('Failed to load workflows:', error);
            toast.error('Failed to load workflows');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectWorkflow = (workflowId) => {
        setSelectedWorkflows(prev => 
            prev.includes(workflowId) 
                ? prev.filter(id => id !== workflowId)
                : [...prev, workflowId]
        );
    };

    const handleSelectAll = () => {
        if (selectedWorkflows.length === workflows.length) {
            setSelectedWorkflows([]);
        } else {
            setSelectedWorkflows(workflows.map(workflow => workflow._id));
        }
    };

    const handleBulkAction = (action) => {
        if (selectedWorkflows.length === 0) return;
        
        const actionMessages = {
            'activate': 'activate',
            'pause': 'pause',
            'deactivate': 'deactivate',
            'delete': 'delete'
        };

        openDialog({
            title: `${actionMessages[action]} Workflows`,
            description: `Are you sure you want to ${actionMessages[action]} ${selectedWorkflows.length} workflow(s)?`,
            confirmText: actionMessages[action].charAt(0).toUpperCase() + actionMessages[action].slice(1),
            cancelText: 'Cancel',
            variant: action === 'delete' ? 'danger' : 'warning',
            onConfirm: async () => {
                try {
                    setBulkActionLoading(true);
                    await workflowAPI.bulkAction(selectedWorkflows, action);
                    toast.success(`${selectedWorkflows.length} workflow(s) ${actionMessages[action]}d successfully`);
                    setSelectedWorkflows([]);
                    loadWorkflows();
                } catch (error) {
                    console.error('Error performing bulk action:', error);
                    toast.error('Failed to perform bulk action');
                } finally {
                    setBulkActionLoading(false);
                }
            }
        });
    };

    const handleWorkflowAction = async (workflowId, action) => {
        try {
            await workflowAPI.updateStatus(workflowId, action);
            toast.success(`Workflow ${action}d successfully!`);
            loadWorkflows();
        } catch (error) {
            console.error(`Error ${action}ing workflow:`, error);
            toast.error(`Failed to ${action} workflow`);
        }
    };

    const handleDeleteWorkflow = (workflowId) => {
        openDialog({
            title: 'Delete Workflow',
            description: 'Are you sure you want to delete this workflow? This action cannot be undone.',
            confirmText: 'Delete Workflow',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await workflowAPI.delete(workflowId);
                    toast.success('Workflow deleted successfully');
                    loadWorkflows();
                } catch (error) {
                    console.error('Error deleting workflow:', error);
                    toast.error('Failed to delete workflow');
                }
            }
        });
    };

    const columns = [
        { accessor: 'select', label: '', sortable: false, width: 'w-12' },
        { accessor: 'name', label: 'Workflow', sortable: true },
        { accessor: 'type', label: 'Type', sortable: true },
        { accessor: 'status', label: 'Status', sortable: true },
        { accessor: 'triggers', label: 'Triggers', sortable: true },
        { accessor: 'actions', label: 'Actions', sortable: true },
        { accessor: 'executions', label: 'Executions', sortable: true },
        { accessor: 'lastRun', label: 'Last Run', sortable: true },
        { accessor: 'workflowActions', label: 'Actions', sortable: false }
    ];

    const statusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'active', label: 'Active' },
        { value: 'paused', label: 'Paused' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'error', label: 'Error' }
    ];

    const typeOptions = [
        { value: '', label: 'All Types' },
        { value: 'lead_nurturing', label: 'Lead Nurturing' },
        { value: 'client_onboarding', label: 'Client Onboarding' },
        { value: 'task_automation', label: 'Task Automation' },
        { value: 'email_sequences', label: 'Email Sequences' },
        { value: 'follow_up', label: 'Follow Up' },
        { value: 'reminder', label: 'Reminder' },
        { value: 'data_sync', label: 'Data Sync' },
        { value: 'notification', label: 'Notification' },
        { value: 'approval', label: 'Approval' },
        { value: 'escalation', label: 'Escalation' }
    ];

    const getActionButton = (workflow) => {
        switch (workflow.status) {
            case 'draft':
                return (
                    <button
                        onClick={() => handleWorkflowAction(workflow._id, 'activate')}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                        title="Activate Workflow"
                    >
                        <PlayIcon className="h-4 w-4" />
                    </button>
                );
            case 'active':
                return (
                    <button
                        onClick={() => handleWorkflowAction(workflow._id, 'pause')}
                        className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                        title="Pause Workflow"
                    >
                        <PauseIcon className="h-4 w-4" />
                    </button>
                );
            case 'paused':
                return (
                    <button
                        onClick={() => handleWorkflowAction(workflow._id, 'activate')}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                        title="Resume Workflow"
                    >
                        <PlayIcon className="h-4 w-4" />
                    </button>
                );
            case 'error':
        return (
                    <button
                        onClick={() => handleWorkflowAction(workflow._id, 'retry')}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        title="Retry Workflow"
                    >
                        <ArrowPathIcon className="h-4 w-4" />
                    </button>
                );
            default:
                return null;
        }
    };

    const formatTriggerDescription = (trigger) => {
        if (!trigger || !trigger.type) return 'No trigger';
        switch (trigger.type) {
            case 'event':
                return `Event: ${trigger.event || 'unknown'}`;
            case 'schedule':
                return `Schedule: ${trigger.schedule?.frequency || 'custom'}`;
            case 'condition':
                return `Condition: ${trigger.conditions?.length || 0} rule(s)`;
            case 'webhook':
                return `Webhook: ${trigger.webhook?.method || 'POST'}`;
            default:
                return trigger.type;
        }
    };

    const formatActionDescription = (actions) => {
        if (!actions || actions.length === 0) return 'No actions';
        
        const actionTypes = actions.map(a => a.type).join(', ');
        return actionTypes.length > 30 ? actionTypes.substring(0, 30) + '...' : actionTypes;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Workflow Automation</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Automate your business processes with intelligent workflows
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 flex items-center gap-3">
                    {selectedWorkflows.length > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleBulkAction('activate')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50"
                            >
                                <PlayIcon className="h-4 w-4 mr-1" />
                                Activate
                            </button>
                    <button 
                                onClick={() => handleBulkAction('pause')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 disabled:opacity-50"
                    >
                                <PauseIcon className="h-4 w-4 mr-1" />
                                Pause
                    </button>
                    <button 
                                onClick={() => handleBulkAction('delete')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                            >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Delete
                            </button>
                        </div>
                    )}
                        <Link
                        to="/workflows/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ backgroundColor: branding?.primaryColor, '--tw-ring-color': branding?.primaryColor }}
                        >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Create Workflow
                    </Link>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <BoltIcon className="h-8 w-8" style={{ color: branding?.primaryColor }} />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Total Workflows</p>
                            <p className="text-2xl font-semibold text-foreground">{workflows.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <CheckCircleIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Active</p>
                            <p className="text-2xl font-semibold text-foreground">
                                {workflows.filter(w => w.status === 'active').length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <ChartBarIcon className="h-8 w-8 text-purple-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Executions Today</p>
                            <p className="text-2xl font-semibold text-foreground">
                                {workflows.reduce((sum, w) => sum + (w.todayExecutions || 0), 0)}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Errors</p>
                            <p className="text-2xl font-semibold text-foreground">
                                {workflows.filter(w => w.status === 'error').length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            style={{ '--tw-ring-color': branding?.primaryColor }}
                        >
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                                            <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Type</label>
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            style={{ '--tw-ring-color': branding?.primaryColor }}
                        >
                            {typeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setStatusFilter('');
                                setTypeFilter('');
                                setSearch('');
                            }}
                            className="w-full px-4 py-2 text-sm font-medium text-foreground bg-muted border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-lg hover:bg-muted"
                        >
                            Clear Filters
                        </button>
                                                </div>
                                                </div>
                                            </div>

            {/* Enhanced Table with Kibo UI */}
            <TableProvider 
                data={workflows} 
                columns={columns}
                onSort={(key, direction) => console.log('🔄 Table sort:', { key, direction })}
                onFilter={(key, value) => console.log('🔍 Table filter:', { key, value })}
            >
                <Table>
                    <TableHeader>
                        <TableSearch placeholder="Search workflows by name, description, or triggers..." />
                    </TableHeader>
                    
                    <div className="bg-card rounded-xl shadow-sm border border-border overflow-x-auto scrollbar-thin">
                        <table className="min-w-[1000px] md:min-w-full divide-y divide-border">
                            <TableHead>
                                <tr>
                                    {columns.map((column) => (
                                        <TableHeaderCell key={column.accessor} column={column}>
                                            {column.accessor === 'select' ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedWorkflows.length === workflows.length && workflows.length > 0}
                                                    onChange={handleSelectAll}
                                                    className="h-4 w-4 border-input rounded focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ color: branding?.primaryColor, '--tw-ring-color': branding?.primaryColor }}
                                                />
                                            ) : (
                                                column.label
                                            )}
                                        </TableHeaderCell>
                                    ))}
                                </tr>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : workflows.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12">
                                            <EmptyStates.NoWorkflows 
                                                onCreateWorkflow={() => navigate('/workflows/new')}
                                                branding={branding}
                                            />
                                        </td>
                                    </tr>
                                ) : (
                                    workflows.map((workflow) => (
                                        <TableRow 
                                            key={workflow._id}
                                            onClick={() => navigate(`/workflows/${workflow._id}`)}
                                            className="cursor-pointer"
                                        >
                                            <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedWorkflows.includes(workflow._id)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectWorkflow(workflow._id);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="h-4 w-4 border-input rounded focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ color: branding?.primaryColor, '--tw-ring-color': branding?.primaryColor }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                            <div className="flex items-center">
                                                    <div className="text-2xl mr-3">{getTypeIcon(workflow.category)}</div>
                                                    <div>
                                                        <span
                                                            className="text-sm font-medium text-foreground"
                                                        >
                                                            {workflow.name}
                                                        </span>
                                                        <div className="text-xs text-muted-foreground">
                                                            {workflow.description}
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {workflow.category ? workflow.category.replace('_', ' ') : 'Unknown'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge 
                                                    status={workflow.status}
                                                    className={getStatusColor(workflow.status)}
                                                >
                                                    {workflow.status.charAt(0).toUpperCase() + workflow.status.slice(1)}
                                                </StatusBadge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    {formatTriggerDescription(workflow.trigger)}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {workflow.trigger && workflow.trigger.type ? 1 : 0} trigger(s)
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    {formatActionDescription(workflow.actions)}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {workflow.actions?.length || 0} action(s)
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    {workflow.totalExecutions || 0} total
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {workflow.todayExecutions || 0} today
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    {workflow.lastRunAt ? 
                                                        new Date(workflow.lastRunAt).toLocaleDateString() : 
                                                        'Never'
                                                    }
                                            </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {workflow.lastRunAt ? 
                                                        new Date(workflow.lastRunAt).toLocaleTimeString() : 
                                                        'Not executed'
                                                    }
                                                </div>
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center gap-2">
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        {getActionButton(workflow)}
                                                    </div>
                                                    <Link
                                                        to={`/workflows/${workflow._id}`}
                                                        className="text-sm font-medium hover:opacity-80"
                                                        style={{ color: branding?.primaryColor }}
                                                        title="View Details"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                    <EyeIcon className="h-4 w-4" />
                                                    </Link>
                                                    <Link
                                                        to={`/workflows/${workflow._id}/edit`}
                                                        className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                        title="Edit"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                    </Link>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteWorkflow(workflow._id);
                                                        }}
                                                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                                                        title="Delete"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                    </table>
                </div>

                    <TableFooter>
                        <div className="px-6 py-3 border-t border-border">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-foreground">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                        </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className="px-3 py-1 text-sm text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1 text-sm text-foreground">
                                        Page {pagination.page} of {pagination.pages}
                                    </span>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page === pagination.pages}
                                        className="px-3 py-1 text-sm text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </TableFooter>
                </Table>
            </TableProvider>
        </div>
    );
};

export default Workflows;